import '@molecules/subscription-box/subscription-box';

import { defineElement, FC, onDidLoad, useElement, useProp } from '@atomify/hooks';
import { Fragment, h } from '@atomify/jsx';
import {
    extendedArea,
    ExtendedAreaModels,
    ExtendedConstructionNumber,
    useStore,
} from '@bpd-library/utilities';
import { Flyin } from '@molecules/fly-in/fly-in';
import { PropertyCard } from '@molecules/property-card/property-card';
import { closeFlyin, openFlyin } from '@source/store/flyins';
import { UserState } from '@source/store/user/utilities/store.types';
import {
    formatConstructionNumberStatus,
    formatLivingArea,
    formatPrice,
    formatRooms,
} from '@source/utilities/api-formatters';
import { useTranslate } from '@source/utilities/hooks';
import { getSubscriptionState } from '@store/subscription/store';
import { getUserWishlist, isLoggedIn, userStore } from '@store/user';

export const FlyinWishlist: FC = () => {
    const [flyinButtonUrl] = useProp<string>('flyinButtonUrl');
    const flyinWishlist = useElement<HTMLElement>('[js-hook-flyin-wishlist]');

    const [, subscribe] = useStore<UserState>(userStore);

    let _data: ExtendedAreaModels | undefined; // Use getData() to use the data, because data isn't fetched onDidLoad toprevent an API call on page load.
    let savedConstructionNumberIds: string[] = [];
    let wishlistUpdatedAfterLogin: boolean = false;

    onDidLoad(async () => {
        if (isLoggedIn()) {
            wishlistUpdatedAfterLogin = true;
            setSavedConstructionNumbers();
        }
    });

    subscribe(
        ({ loggedIn }) => {
            if (loggedIn) {
                setSavedConstructionNumbers();
            } else {
                savedConstructionNumberIds = [];
                wishlistUpdatedAfterLogin = false;
            }
        },
        ['loggedIn'],
    );

    subscribe(
        ({ wishlist }) => {
            // Initial update will come from from logging in
            // This avoids the flyin from being opened right after logging in
            if (wishlistUpdatedAfterLogin) {
                const newConstructionNumberIds =
                    wishlist?.filter((id) => savedConstructionNumberIds?.indexOf(id) === -1) || [];

                if (newConstructionNumberIds.length) {
                    renderWishlistPropertyCards(newConstructionNumberIds);
                    openFlyin('flyin-wishlist');
                } else {
                    closeFlyin('flyin-wishlist');
                    setSavedConstructionNumbers();
                }
            }

            wishlistUpdatedAfterLogin = true;
        },
        ['wishlist'],
    );

    const getData = async (): Promise<ExtendedAreaModels | undefined> => {
        if (!_data) _data = await extendedArea.getData(window.__ENVIRONMENT_DETAILS__.areaId);

        return _data;
    };

    const setSavedConstructionNumbers = () => {
        const wishlist = getUserWishlist();

        savedConstructionNumberIds = wishlist ? [...wishlist] : [];
    };

    const getUnSubscribedConstructionNumbers = (
        constructionNumbers: ExtendedConstructionNumber[],
    ): ExtendedConstructionNumber[] => {
        const subscribedProjects = getSubscriptionState().projectPhases || [];

        const projectsContain = (projectPhaseId: string) =>
            !!subscribedProjects.find((id) => id === projectPhaseId);

        return constructionNumbers.reduce(
            (unsubscribed: ExtendedConstructionNumber[], constructionNumber) => {
                const projectPhaseId = constructionNumber.projectPhase?.id;

                const isSubscribed = projectPhaseId ? projectsContain(projectPhaseId) : false;

                if (!isSubscribed) unsubscribed.push(constructionNumber);

                return unsubscribed;
            },
            [],
        );
    };

    const renderWishlistPropertyCards = async (constructionNumberIds: string[]) => {
        const data = await getData();

        if (!data) return;

        const constructionNumbers = data.constructionNumbers.filter(({ id }) =>
            constructionNumberIds.includes(id),
        );

        const unSubscribedConstructionNumbers =
            getUnSubscribedConstructionNumbers(constructionNumbers);

        flyinWishlist.current!.innerHTML = '';

        if (unSubscribedConstructionNumbers.length)
            renderSubscriptionBox(unSubscribedConstructionNumbers[0]);

        flyinWishlist.current!.append(
            <Fragment>
                {constructionNumbers.map((constructionNumber) => {
                    const {
                        specifications,
                        propertyTypeName,
                        mainImage,
                        identifier,
                        projectPhaseName,
                    } = constructionNumber;

                    if (!propertyTypeName) return;

                    const specs: string[] = [];

                    if (specifications) {
                        const rooms = formatRooms(constructionNumber);
                        const livingArea = formatLivingArea(constructionNumber);

                        if (rooms) specs.push(rooms.value);
                        if (livingArea) specs.push(livingArea.value);
                    }

                    return (
                        <PropertyCard
                            classes="flyin-wishlist__card"
                            number={identifier}
                            title={propertyTypeName}
                            project={projectPhaseName}
                            image={mainImage?.url}
                            price={formatPrice({ constructionNumber })}
                            specs={specs}
                            status={formatConstructionNumberStatus(constructionNumber)}
                            orientation="vertical"
                        />
                    );
                })}
            </Fragment>,
        );
    };

    const renderSubscriptionBox = (constructionNumber: ExtendedConstructionNumber) => {
        const { districtId, projectPhase } = constructionNumber;

        if (
            !districtId ||
            !projectPhase ||
            projectPhase.status === 'Rented' ||
            projectPhase.status === 'Sold'
        )
            return;

        flyinWishlist.current!.append(
            <subscription-box
                class="flyin-wishlist__subscription-box"
                boxContent={useTranslate('SubscriptionBox.Intro', {
                    replace: [{ id: 'ProjectName', value: projectPhase.name || '' }],
                })}
                projectPhaseId={projectPhase.id}
                projectPhaseName={projectPhase.name}
                districtId={districtId}
                buttonLabel={useTranslate('SubscriptionBox.ButtonLabel')}
                buttonVariant="tertiary"
                variant="vertical"
                deleteWhenSubscribed={true}
            />,
        );
    };

    return (
        <Flyin
            id="flyin-wishlist"
            label={useTranslate('FlyInHeader.Text')}
            buttonLabel={useTranslate('ToWishlist.Text')}
            buttonLink={flyinButtonUrl}>
            <div js-hook-flyin-wishlist></div>
        </Flyin>
    );
};

FlyinWishlist.props = {
    flyinButtonUrl: {
        type: String,
        required: true,
    },
};

defineElement('bpd-flyin-wishlist', FlyinWishlist);
